import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { playBeep } from 'src/app/shared/helpers/audio.helper';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CreateChunkOptionsOverlayData } from 'src/app/views/content/chunk/create-chunk/create-chunk-options-overlay/create-chunk-options-overlay.component';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { ScreenRecorderReviewDialogComponent } from './dialog/screen-recorder-review-dialog.component';
import { ScreenRecorderFloatingControlsComponent } from './screen-recorder-floating-controls.component';

declare let MediaRecorder: any;

@Injectable({
  providedIn: 'root',
})
export class ScreenRecorderService implements OnDestroy {
  isRecording = signal(false);
  isCountingDown = signal(false);
  countdown = signal<number | null>(null);
  stream = signal<MediaStream | null>(null);

  private mediaRecorder: any;
  private combinedStream: MediaStream | null = null;
  private recordedBlobs: Blob[] = [];
  private floatingOverlayRef: OverlayRef | null = null;
  private currentData: CreateChunkOptionsOverlayData | null = null;
  private countdownIntervalId: any = null;

  private dialog = inject(MatDialog);
  private overlay = inject(Overlay);
  private contentStateService = inject(ContentStateService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  ngOnDestroy(): void {
    this.clearCountdownInterval();
    this.stopAllStreams();
    this.removeFloatingControls();
  }

  async startScreenCapture(microphoneId: string, data: CreateChunkOptionsOverlayData): Promise<void> {
    this.currentData = data;
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const voiceStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: microphoneId } } });

      const videoTrack = displayStream.getVideoTracks()[0];
      const audioTrack = voiceStream.getAudioTracks()[0];

      this.combinedStream = new MediaStream([videoTrack, audioTrack]);
      if (displayStream.getAudioTracks()[0]) {
        this.combinedStream.addTrack(displayStream.getAudioTracks()[0]);
      }
      this.stream.set(this.combinedStream);

      // Handle the case where the user stops sharing via Chrome's native stop sharing UI
      videoTrack.onended = () => {
        this.stopRecording();
      };

      this.startRecordingWithCountdown();
    } catch (err: any) {
      console.error('Screen capture was cancelled or failed.', err);
      const isUserCancellation = err.name === 'NotAllowedError' || err.message?.includes('Permission denied');
      if (!isUserCancellation) {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'An error occurred', 'OK', { duration: 3000 });
      }
      this.stopAllStreams();
    }
  }

  private startRecordingWithCountdown(): void {
    this.isCountingDown.set(true);
    this.countdown.set(3);
    this.showFloatingControls();
    playBeep(880);
    this.countdownIntervalId = setInterval(() => {
      const current = this.countdown();
      if (current !== null && current > 1) {
        this.countdown.set(current - 1);
        playBeep(880);
      } else {
        this.clearCountdownInterval();
        this.isCountingDown.set(false);
        this.countdown.set(null);
        playBeep(1200, true);
        this.beginActualRecording();
      }
    }, 1000);
  }

  private clearCountdownInterval(): void {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  private beginActualRecording(): void {
    if (!this.combinedStream) return;
    this.isRecording.set(true);
    this.recordedBlobs = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.combinedStream, { mimeType: 'video/webm' });
      this.mediaRecorder.ondataavailable = (event: any) => {
        if (event.data && event.data.size > 0) {
          this.recordedBlobs.push(event.data);
        }
      };
      this.mediaRecorder.onstop = () => {
        const finalBlob = new Blob(this.recordedBlobs, { type: 'video/webm' });
        this.stopAllStreams();
        this.removeFloatingControls();
        this.openReviewDialog(finalBlob);
        this.isRecording.set(false);
      };
      this.mediaRecorder.start();

      // Show floating stop controls on screen
      this.showFloatingControls();
    } catch (err) {
      console.error('Error creating MediaRecorder:', err);
      this.isRecording.set(false);
      this.stopAllStreams();
      this.removeFloatingControls();
    }
  }

  stopRecording(): void {
    this.clearCountdownInterval();
    this.isCountingDown.set(false);
    this.countdown.set(null);
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    } else {
      this.stopAllStreams();
      this.removeFloatingControls();
      this.isRecording.set(false);
    }
  }

  private stopAllStreams(): void {
    if (this.combinedStream) {
      this.combinedStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.combinedStream = null;
      this.stream.set(null);
    }
  }

  private showFloatingControls(): void {
    this.removeFloatingControls();

    const positionStrategy = this.overlay.position().global().right('24px').bottom('24px');

    this.floatingOverlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });

    const portal = new ComponentPortal(ScreenRecorderFloatingControlsComponent);
    this.floatingOverlayRef.attach(portal);
  }

  private removeFloatingControls(): void {
    if (this.floatingOverlayRef) {
      this.floatingOverlayRef.dispose();
      this.floatingOverlayRef = null;
    }
  }

  private openReviewDialog(blob: Blob): void {
    const dialogRef = this.dialog.open(ScreenRecorderReviewDialogComponent, {
      data: blob,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: 'save' | 'discard' | undefined) => {
      if (result === 'save') {
        this.contentStateService.uploadRecording(blob, 'video', this.currentData?.chunkAboveId);
      }
    });
  }
}
